import { logger } from '@app/common/logger/logger'

import { getClientTabId } from './clientTabId'

const REFRESH_LOCK_NAME = 'lawyer-portal-refresh-token'
const REFRESH_LOCK_STORAGE_KEY = 'refreshLock'
const LOCK_HOLD_EXPIRY_MS = 10 * 1000
const LOCK_POLL_INTERVAL_MS = 50
const LOCK_WAIT_TIMEOUT_MS = 5 * 1000

type StoredRefreshLock = {
  tabId: string
  acquiredAt: number
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const readStoredLock = (): StoredRefreshLock | undefined => {
  const stored = localStorage.getItem(REFRESH_LOCK_STORAGE_KEY)

  if (!stored) {
    return undefined
  }

  try {
    return JSON.parse(stored)
  } catch {
    return undefined
  }
}

const isHeldByAnotherTab = (tabId: string): boolean => {
  const storedLock = readStoredLock()

  return Boolean(
    storedLock &&
      storedLock.tabId !== tabId &&
      Date.now() - storedLock.acquiredAt < LOCK_HOLD_EXPIRY_MS
  )
}

const acquireStoredLock = (tabId: string): boolean => {
  if (isHeldByAnotherTab(tabId)) {
    return false
  }

  localStorage.setItem(REFRESH_LOCK_STORAGE_KEY, JSON.stringify({ tabId, acquiredAt: Date.now() }))

  return readStoredLock()?.tabId === tabId
}

const releaseStoredLock = (tabId: string): void => {
  if (readStoredLock()?.tabId === tabId) {
    localStorage.removeItem(REFRESH_LOCK_STORAGE_KEY)
  }
}

const withStoredLock = async <T>(runExclusively: () => Promise<T>): Promise<T> => {
  const tabId = getClientTabId()
  const waitUntil = Date.now() + LOCK_WAIT_TIMEOUT_MS
  logger.info(`polyfill navigator.locks for browser that does not support it, tabId: ${tabId}`)

  while (!acquireStoredLock(tabId)) {
    if (Date.now() >= waitUntil) {
      logger.info(`tab ${tabId} gave up waiting for the stored refresh lock of another tab`)
      break
    }

    await delay(LOCK_POLL_INTERVAL_MS)
  }

  try {
    return await runExclusively()
  } finally {
    releaseStoredLock(tabId)
  }
}

export const withRefreshLock = <T>(runExclusively: () => Promise<T>): Promise<T> =>
  navigator.locks
    ? navigator.locks.request(REFRESH_LOCK_NAME, runExclusively)
    : withStoredLock(runExclusively)
